import * as React from 'react';
import {
  Button as BButton,
  ButtonProps as BButtonProps,
} from 'react-bootstrap';
import { Icon } from '../index';
import clsx from 'clsx';
import './button.scss';
const __DEFAULT_ELEMENT__ = BButton;
export type ButtonVariant =
  | 'default' // no style will be applied
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'tertiary-blue'
  | 'tertiary-grey'
  | 'light'
  | 'neutral'
  | 'outline'
  | 'outline-secondary'
  | 'outline-primary'
  | 'white-chip'
  | 'link';

type ButtonOwnProps<E extends React.ElementType = typeof __DEFAULT_ELEMENT__> =
  Omit<BButtonProps, 'as' | 'style' | 'variant' | 'size'> & {
    as?: E;
    variant: ButtonVariant;
    isLoading?: boolean;
    id: string;
    size?: 'xxs' | 'xs' | 'md' | 'sm' | 'lg';
    fullWidth?: boolean;
    rounded?: boolean;
  };

export type ButtonProps<E extends React.ElementType> = ButtonOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ButtonOwnProps>;

function Button<T extends React.ElementType = typeof __DEFAULT_ELEMENT__>({
  children,
  as,
  variant,
  size = 'md',
  id,
  isLoading,
  disabled,
  fullWidth,
  rounded,
  className: extraClasses,
  ...props
}: ButtonProps<T>) {
  const ComponentToUse = as || __DEFAULT_ELEMENT__;

  return (
    <ComponentToUse
      {...props}
      disabled={disabled || isLoading}
      id={id}
      data-cy={id}
      variant={variant}
      className={clsx(
        extraClasses,
        'd-flex justify-content-center align-items-center text-wrap position-relative btn',
        {
          'w-100': fullWidth,
          'disabled': disabled,
          'btn-rounded': rounded,
          'btn-primary': variant === 'primary',
          'btn-secondary': variant === 'secondary',
          'btn-danger': variant === 'danger',
          'btn-success': variant === 'success',
          'btn-warning': variant === 'warning',
          'btn-tertiary-blue': variant === 'tertiary-blue',
          'btn-tertiary-grey': variant === 'tertiary-grey',
          'btn-neutral': variant === 'neutral',
          'btn-outline': variant === 'outline',
          'btn-outline-secondary': variant === 'outline-secondary',
          'btn-outline-primary': variant === 'outline-primary',
          'btn-white-chip': variant === 'white-chip',
          'btn-link': variant === 'link',
          'btn-light': variant === 'light',
          'btn-xxs': size === 'xxs',
          'btn-xs': size === 'xs',
          'btn-sm': size === 'sm',
          'btn-md': size === 'md',
          'btn-lg': size === 'lg',
        },
      )}
    >
      {children}
      {isLoading && (
        <div
          className='d-flex align-items-center position-absolute'
          style={{
            top: '50%',
            width: '100%',
            right: 0,
            transform: 'translateY(-50%)',
          }}
        >
          <Icon
            className='position-absolute'
            icon='spinner'
            style={{ width: 15, height: 15, right: '5%', opacity: 0.7 }}
          />
        </div>
      )}
    </ComponentToUse>
  );
}

export default Button;

Button.displayName = 'Button';
